import { useEffect, useRef, useState } from "react";
import { ChevronLeft, Loader2 } from "lucide-react";

const OTP_LENGTH = 6;

function secondsUntil(timestamp) {
  if (!timestamp) return 0;
  return Math.max(0, Math.ceil((new Date(timestamp).getTime() - Date.now()) / 1000));
}

export function VerifyStep({
  onVerify,
  onResend,
  onBack,
  email,
  verificationId,
  resendAvailableAt,
  loading = false,
  resending = false,
  error,
}) {
  const [otp, setOtp] = useState(Array(OTP_LENGTH).fill(""));
  const [timer, setTimer] = useState(() => secondsUntil(resendAvailableAt));
  const [localError, setLocalError] = useState("");
  const inputs = useRef([]);
  const submitInFlight = useRef(false);

  useEffect(() => {
    inputs.current[0]?.focus();
  }, []);

  useEffect(() => {
    setTimer(secondsUntil(resendAvailableAt));
  }, [resendAvailableAt]);

  useEffect(() => {
    if (timer <= 0) return undefined;
    const interval = setInterval(() => setTimer((value) => Math.max(0, value - 1)), 1000);
    return () => clearInterval(interval);
  }, [timer]);

  const displayError = localError || (typeof error === "string" ? error : error?.message) || "";

  const submitCode = async (code) => {
    if (!verificationId || code.length !== OTP_LENGTH || loading || submitInFlight.current) return;
    submitInFlight.current = true;
    setLocalError("");
    try {
      await onVerify(code);
    } catch {
      setOtp(Array(OTP_LENGTH).fill(""));
      inputs.current[0]?.focus();
    } finally {
      submitInFlight.current = false;
    }
  };

  const handleChange = (index, value) => {
    if (!/^\d*$/.test(value)) return;
    const nextOtp = [...otp];
    nextOtp[index] = value.slice(-1);
    setOtp(nextOtp);

    if (value && index < OTP_LENGTH - 1) {
      inputs.current[index + 1]?.focus();
    }
    if (nextOtp.every(Boolean)) {
      submitCode(nextOtp.join(""));
    }
  };

  const handleKeyDown = (index, event) => {
    if (event.key === "Backspace" && !otp[index] && index > 0) {
      inputs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (event) => {
    event.preventDefault();
    const pasted = event.clipboardData.getData("text").trim().slice(0, OTP_LENGTH);
    if (!/^\d{6}$/.test(pasted)) {
      setLocalError("Paste the complete six-digit code.");
      return;
    }
    const nextOtp = pasted.split("");
    setOtp(nextOtp);
    inputs.current[OTP_LENGTH - 1]?.focus();
    submitCode(pasted);
  };

  const handleResend = async () => {
    if (timer > 0 || resending || !verificationId) return;
    setLocalError("");
    try {
      const challenge = await onResend();
      setTimer(secondsUntil(challenge.resend_available_at));
      setOtp(Array(OTP_LENGTH).fill(""));
      inputs.current[0]?.focus();
    } catch {
      // React Query exposes the normalized server message via `error`.
    }
  };

  const isComplete = otp.every(Boolean);

  return (
    <div className="flex flex-col h-full w-full max-w-xl mx-auto bg-[#FDFDFD] p-5 md:p-8 text-left">
      <button
        type="button"
        onClick={onBack}
        disabled={loading || resending}
        aria-label="Back"
        className="mb-6 w-10 h-10 rounded-full border border-slate-200 bg-white flex items-center justify-center active:scale-95 transition text-[#0A4730]/60 hover:border-[#0A4730]/30 shadow-sm disabled:opacity-50"
      >
        <ChevronLeft size={20} />
      </button>

      <div className="mb-8 text-center">
        <h1 className="text-2xl text-[#0A4730] font-serif font-semibold tracking-tight mb-2">
          Verify Email
        </h1>
        <p className="text-slate-500 text-sm leading-relaxed max-w-sm mx-auto">
          Enter the six-digit code sent to{" "}
          <span className="font-semibold text-[#0A2540]">{email}</span>
        </p>
      </div>

      <div className="space-y-6 flex-grow">
        <div className="bg-white rounded-3xl p-5 border border-slate-100 shadow-[0_4px_20px_-5px_rgba(0,0,0,0.05)] space-y-6">
          <div className="flex justify-center gap-2" onPaste={handlePaste}>
            {otp.map((digit, index) => (
              <input
                key={index}
                ref={(element) => (inputs.current[index] = element)}
                type="text"
                inputMode="numeric"
                autoComplete={index === 0 ? "one-time-code" : "off"}
                aria-label={`Verification code digit ${index + 1}`}
                maxLength={1}
                value={digit}
                disabled={loading}
                onChange={(event) => handleChange(index, event.target.value)}
                onKeyDown={(event) => handleKeyDown(index, event)}
                className="w-11 h-14 md:w-14 md:h-16 rounded-2xl border border-slate-200 bg-white text-center text-xl font-bold text-[#0A2540] outline-none shadow-sm transition-all focus:border-[#0A4730] focus:ring-4 focus:ring-[#0A4730]/5 disabled:opacity-60"
              />
            ))}
          </div>

          {displayError && (
            <div role="alert" className="rounded-2xl bg-red-50 border border-red-100 p-3 text-red-600 text-xs text-center">
              {displayError}
            </div>
          )}

          <button
            type="button"
            disabled={!isComplete || loading || !verificationId}
            onClick={() => submitCode(otp.join(""))}
            className="w-full h-14 rounded-2xl font-bold tracking-wide text-white bg-[#0A4730] hover:bg-[#0A4730]/90 active:scale-[0.98] transition-all shadow-lg disabled:bg-slate-200 disabled:opacity-60 flex items-center justify-center gap-2"
          >
            {loading && <Loader2 size={16} className="animate-spin" />}
            {loading ? "Verifying..." : "Verify email"}
          </button>
        </div>
      </div>

      <div className="mt-8 text-center text-sm">
        <span className="text-slate-500">Did not receive the code? </span>
        <button
          type="button"
          onClick={handleResend}
          disabled={timer > 0 || resending || !verificationId}
          className={`font-bold transition-colors ${
            timer > 0 || resending
              ? "text-slate-400 cursor-not-allowed"
              : "text-[#0A4730] hover:underline"
          }`}
        >
          {resending ? "Sending..." : timer > 0 ? `Resend in ${timer}s` : "Resend code"}
        </button>
      </div>
    </div>
  );
}
